import { useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import * as styles from '../FirstResponder.module.css';
import { LiveJobRow, LiveTone } from './liveViewModel';
import { formatCountdown } from './useCountdown';

/*
 * One row of the jobs table (GRAVY-10816 follow-up). Extracted from JobsCard.tsx's inline
 * `rows.map(...)` so a reopened job's row can own its own expand state — the main row's
 * rendering below is UNCHANGED from before this follow-up.
 *
 * The chevron appears ONLY when `j.history.length > 0` (a job that reopened at least once),
 * collapsed by default, same affordance NeedsApprovalRow.tsx already proved on the Dashboard V2
 * queue. Expanding costs zero network calls — `history` already arrived with the jobs-list
 * payload (Correction 5). The "Open" button on an individual history entry still runs the
 * existing detail fetch (`onSelectJob(j.id, 'overview', h.runId)`), exactly as opening the main
 * row already does — only the list of history itself is free.
 */

const NOT_YET_EVALUATED_TITLE =
  'No evaluation has been recorded for this job yet';

const BADGE_TONE: Record<LiveTone, string | undefined> = {
  blue: styles.blue,
  amber: styles.amber,
  green: styles.green,
  red: styles.red,
  plain: undefined,
};

type Props = {
  j: LiveJobRow;
  onSelectJob: (
    id: string,
    section?: 'overview' | 'workers',
    runId?: string | null,
  ) => void;
  elapsed: number;
};

const JobRow = ({ j, onSelectJob, elapsed }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const hasHistory = j.history.length > 0;

  return (
    <>
      <tr className={styles.zebra} data-testid={`flp-job-row-${j.id}`}>
        <td className={styles.strong}>
          {hasHistory && (
            <button
              type="button"
              className={styles.btnPlain}
              style={{ marginRight: 6, verticalAlign: 'middle' }}
              data-testid={`flp-job-history-toggle-${j.id}`}
              aria-label={
                expanded
                  ? 'Collapse earlier activations'
                  : 'Expand earlier activations'
              }
              aria-expanded={expanded}
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded ? (
                <ChevronDownIcon width={14} height={14} />
              ) : (
                <ChevronRightIcon width={14} height={14} />
              )}
            </button>
          )}
          {j.clickable ? (
            <button
              type="button"
              className={styles.btnPlain}
              style={{
                fontSize: 'inherit',
                color: 'inherit',
                fontWeight: 500,
              }}
              data-testid={`flp-job-open-${j.id}`}
              onClick={() => onSelectJob(j.id, 'overview', j.runId)}
            >
              {j.title}
            </button>
          ) : (
            <span
              data-testid={`flp-job-open-${j.id}`}
              title={NOT_YET_EVALUATED_TITLE}
            >
              {j.title}
            </span>
          )}
        </td>
        <td className={styles.muted}>{j.client}</td>
        <td>
          <span className={`${styles.badge} ${BADGE_TONE[j.tone] ?? ''}`}>
            {j.stateLabel}
          </span>
        </td>
        <td>
          {j.next.kind === 'countdown' ? (
            <span className={styles.strong} data-testid={`flp-countdown-${j.id}`}>
              {formatCountdown(
                j.next.secondsRemaining,
                elapsed,
                j.next.purpose,
              )}
            </span>
          ) : (
            <span className={styles.muted}>{j.next.text}</span>
          )}
        </td>
        <td>
          {j.workersAvailable === null ? (
            <span className={styles.muted} data-testid={`flp-workers-${j.id}`}>
              —
            </span>
          ) : j.clickable ? (
            <button
              type="button"
              className={styles.btnPlain}
              style={{ fontSize: 'inherit' }}
              data-testid={`flp-workers-${j.id}`}
              aria-label={`View ${j.workersAvailable} workers available for ${j.title}`}
              onClick={() => onSelectJob(j.id, 'workers', j.runId)}
            >
              {j.workersAvailable}
            </button>
          ) : (
            <span data-testid={`flp-workers-${j.id}`} title={NOT_YET_EVALUATED_TITLE}>
              {j.workersAvailable}
            </span>
          )}
        </td>
      </tr>
      {expanded && hasHistory && (
        <tr className={styles.zebra} data-testid={`flp-job-history-${j.id}`}>
          <td colSpan={5}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {j.history.map((h) => (
                <div
                  key={h.runId}
                  data-testid={`flp-job-history-entry-${h.runId}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: '0.8125rem',
                  }}
                >
                  <span className={`${styles.badge} ${BADGE_TONE[h.tone] ?? ''}`}>
                    {h.stateLabel}
                  </span>
                  <span className={styles.muted}>{h.anchorSourceLabel}</span>
                  <span className={styles.muted}>{h.anchorAt}</span>
                  {h.stateReason && (
                    <span className={styles.muted}>
                      {h.stateReason.replace(/_/g, ' ')}
                    </span>
                  )}
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`}
                    data-testid={`flp-job-history-open-${h.runId}`}
                    onClick={() => onSelectJob(j.id, 'overview', h.runId)}
                  >
                    Open
                  </button>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default JobRow;
